import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Button } from '@vaiu/ui/button';
import { Input } from '@vaiu/ui/input';
import { Label } from '@vaiu/ui/label';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import GoogleSignIn from './GoogleSignIn';
import BiometricLogin from './BiometricLogin';
import { i18n } from '@/utils/i18n';
import {
  Gift,
  DollarSign,
  Lock,
  UserPlus,
  LogIn,
  ArrowRight,
  CheckCircle,
  AlertCircle,
  Sparkles,
  CreditCard,
  Smartphone,
  Globe,
  Shield,
  Clock
} from 'lucide-react';

interface TokenInfo {
  amount: number;
  currency: string;
  message?: string;
  creator?: string;
  type?: 'cash' | 'invoice';
  expiresAt?: string;
}

interface TokenRedemptionLandingProps {
  tokenCode: string;
  onAuthSuccess: (token: string) => void;
  onBackToLogin: () => void;
}

const TokenRedemptionLanding: React.FC<TokenRedemptionLandingProps> = ({
  tokenCode,
  onAuthSuccess,
  onBackToLogin
}) => {
  const navigate = useNavigate();
  const { login, isGuest } = useAuth();
  const { toast } = useToast();
  
  const [tokenInfo, setTokenInfo] = useState<TokenInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [authMode, setAuthMode] = useState<'preview' | 'login' | 'signup'>('preview');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Login form state
  const [loginData, setLoginData] = useState({
    email: '',
    password: ''
  });
  
  // Signup form state
  const [signupData, setSignupData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });

  useEffect(() => {
    fetchTokenInfo();
  }, [tokenCode]);

  const fetchTokenInfo = async () => {
    if (!tokenCode) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // For now, show a generic token preview since getTokenInfo doesn't exist
      // In a real implementation, this would fetch token metadata
      setTokenInfo({
        amount: 0,
        currency: 'USD',
        message: 'Payment token ready for redemption',
        type: 'cash'
      });
    } catch (err: any) {
      console.error('Failed to fetch token info:', err);
      
      // For expired or invalid tokens, show appropriate message
      if (err.message?.includes('expired') || err.message?.includes('invalid')) {
        setError('This token has expired or is invalid.');
      } else {
        // For other errors, still show a generic preview
        setTokenInfo({
          amount: 0,
          currency: 'USD',
          message: 'Payment token ready for redemption',
          type: 'cash'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      default: return currency + ' ';
    }
  };

  const getTimeRemaining = (expiresAt: string) => {
    if (!expiresAt) return null;
    
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = expires.getTime() - now.getTime();
    
    if (diff <= 0) return 'Expired';
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days} day${days > 1 ? 's' : ''} left`;
    }
    
    if (hours > 0) {
      return `${hours}h ${minutes}m left`;
    }
    
    return `${minutes}m left`;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({ email: '', password: '', confirmPassword: '' });
    setIsSubmitting(true);

    try {
      const result = await platformApi.login(loginData.email.trim(), loginData.password);
      
      if (result.token) {
        await login(result.token);
        onAuthSuccess(result.token);
        
        toast({
          title: "Login Successful!",
          description: "Now redeeming your token...",
        });
      } else {
        throw new Error('No authentication token received');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      
      let errorMessage = err.message || 'Login failed';
      if (errorMessage.includes('Authentication required') || errorMessage.includes('401')) {
        errorMessage = 'Invalid email or password';
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({ email: '', password: '', confirmPassword: '' });

    // Validate form
    const errors = { email: '', password: '', confirmPassword: '' };
    let isValid = true;

    if (!signupData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(signupData.email)) {
      errors.email = 'Please enter a valid email address';
      isValid = false;
    }

    if (!signupData.password) {
      errors.password = 'Password is required';
      isValid = false;
    }

    if (signupData.password !== signupData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    if (!isValid) {
      setFieldErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      const requestBody: any = {
        username: signupData.email,
        email: signupData.email,
        password: signupData.password,
        source: 'token_redemption',
        tokenCode: tokenCode
      };

      // If user is currently a guest, include guest token to preserve UUID and data
      if (isGuest) {
        const guestToken = platformApi.getToken();
        if (guestToken) {
          requestBody.guestToken = guestToken;
        }
      }

      const response = await fetch('/api/v1/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || data.message || 'Registration failed');
      }

      // Auto-login after successful registration
      const loginResult = await platformApi.login(signupData.email, signupData.password);
      
      if (loginResult.token) {
        await login(loginResult.token);
        onAuthSuccess(loginResult.token);
        
        toast({
          title: "Account Created!",
          description: "Welcome to Vaiu! Now redeeming your token...",
        });
      } else {
        throw new Error('Registration successful but login failed');
      }
    } catch (err: any) {
      console.error('Signup error:', err);
      
      let errorMessage = err.message || 'Registration failed';
      if (errorMessage.includes('already exists') || errorMessage.includes('already registered')) {
        errorMessage = 'An account with this email already exists. Try logging in instead.';
      }
      
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGoogleSuccess = async (token: string) => {
    try {
      await login(token);
      onAuthSuccess(token);
      
      toast({
        title: "Login Successful!",
        description: "Now redeeming your token...",
      });
    } catch (err: any) {
      console.error('Google login error:', err);
      setError('Google login was successful but session setup failed. Please try again.');
    }
  };

  const handleBiometricSuccess = async (token: string) => {
    try {
      await login(token);
      onAuthSuccess(token);
      
      toast({
        title: "Biometric Login Successful!",
        description: "Now redeeming your token...",
      });
    } catch (err: any) {
      console.error('Biometric login error:', err);
      setError('Biometric authentication failed. Please try another method.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading your token...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !tokenInfo) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8">
            <div className="text-center space-y-4">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
              <h2 className="text-xl font-bold text-gray-900">Token Not Available</h2>
              <p className="text-gray-600">{error}</p>
              <Button onClick={() => navigate('/')} className="w-full">
                Go to Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        
        {/* Token Preview Card */}
        <Card className="border-2 border-green-200 shadow-lg">
          <CardHeader className="text-center pb-4">
            <div className="flex justify-center mb-3">
              <div className="p-3 bg-green-100 rounded-full">
                <Gift className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-gray-900">
              You've Received a Payment!
            </CardTitle>
            <CardDescription className="text-base">
              Someone has sent you a digital payment token
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            {tokenInfo && (
              <>
                <div className="p-4 bg-gradient-to-r from-green-100 to-blue-100 rounded-lg">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <DollarSign className="w-6 h-6 text-green-600" />
                    <span className="text-3xl font-bold text-gray-900">
                      {getCurrencySymbol(tokenInfo.currency)}{tokenInfo.amount > 0 ? tokenInfo.amount.toFixed(2) : '•••'}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">
                    {tokenInfo.currency} • {tokenInfo.type === 'invoice' ? 'Invoice Payment' : 'Cash Token'}
                  </p>
                </div>
                
                {tokenInfo.message && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-800 italic">
                      "{tokenInfo.message}"
                    </p>
                  </div>
                )}
                
                {tokenInfo.creator && (
                  <p className="text-sm text-gray-600">
                    From: <span className="font-medium">@{tokenInfo.creator}</span>
                  </p>
                )}
                
                {tokenInfo.expiresAt && (
                  <div className="flex items-center justify-center space-x-2 text-sm text-amber-600">
                    <Clock className="w-4 h-4" />
                    <span>{getTimeRemaining(tokenInfo.expiresAt)}</span>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Authentication Card */}
        <Card className="shadow-lg">
          <CardContent className="p-6">
            {authMode === 'preview' && (
              <div className="space-y-4">
                <div className="text-center space-y-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    Claim Your Payment
                  </h3>
                  <p className="text-sm text-gray-600">
                    You need a Vaiu account to redeem this token. It's free and takes less than a minute!
                  </p>
                </div>
                
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => setAuthMode('signup')}
                      className="flex items-center justify-center space-x-2"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Sign Up</span>
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setAuthMode('login')}
                      className="flex items-center justify-center space-x-2"
                    >
                      <LogIn className="w-4 h-4" />
                      <span>Log In</span>
                    </Button>
                  </div>
                  
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white px-2 text-muted-foreground">or</span>
                    </div>
                  </div>
                  
                  <GoogleSignIn
                    onSuccess={handleGoogleSuccess}
                    onError={(error) => setError(error)}
                    disabled={isSubmitting}
                  />
                  
                  <BiometricLogin
                    onSuccess={handleBiometricSuccess}
                    onError={(error) => setError(error)}
                    disabled={isSubmitting}
                  />
                </div>
                
                <div className="text-center pt-4">
                  <div className="flex items-center justify-center space-x-4 text-xs text-gray-500">
                    <div className="flex items-center space-x-1">
                      <Shield className="w-3 h-3" />
                      <span>Secure</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Sparkles className="w-3 h-3" />
                      <span>Free</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <Globe className="w-3 h-3" />
                      <span>Global</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {authMode === 'login' && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900">Welcome Back</h3>
                  <p className="text-sm text-gray-600">Log in to redeem your token</p>
                </div>
                
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@example.com"
                      value={loginData.email}
                      onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                      className={fieldErrors.email ? 'border-red-500' : ''}
                      required
                    />
                    {fieldErrors.email && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Your password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                      className={fieldErrors.password ? 'border-red-500' : ''}
                      required
                    />
                    {fieldErrors.password && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.password}</p>
                    )}
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Logging in...' : 'Log In & Redeem'}
                  </Button>
                </form>
                
                <div className="text-center space-y-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAuthMode('signup')}
                  >
                    Don't have an account? Sign up
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAuthMode('preview')}
                  >
                    Back to options
                  </Button>
                </div>
              </div>
            )}
            
            {authMode === 'signup' && (
              <div className="space-y-4">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900">Create Your Account</h3>
                  <p className="text-sm text-gray-600">Join Vaiu to redeem your token</p>
                </div>
                
                <form onSubmit={handleSignup} className="space-y-4">
                  <div>
                    <Label htmlFor="signup-email">Email</Label>
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="you@example.com"
                      value={signupData.email}
                      onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                      className={fieldErrors.email ? 'border-red-500' : ''}
                      required
                    />
                    {fieldErrors.email && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.email}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="signup-password">Password</Label>
                    <Input
                      id="signup-password"
                      type="password"
                      placeholder="Choose a password"
                      value={signupData.password}
                      onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                      className={fieldErrors.password ? 'border-red-500' : ''}
                      required
                    />
                    {fieldErrors.password && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.password}</p>
                    )}
                  </div>
                  
                  <div>
                    <Label htmlFor="confirm-password">Confirm Password</Label>
                    <Input
                      id="confirm-password"
                      type="password"
                      placeholder="Confirm your password"
                      value={signupData.confirmPassword}
                      onChange={(e) => setSignupData({...signupData, confirmPassword: e.target.value})}
                      className={fieldErrors.confirmPassword ? 'border-red-500' : ''}
                      required
                    />
                    {fieldErrors.confirmPassword && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors.confirmPassword}</p>
                    )}
                  </div>
                  
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating Account...' : 'Create Account & Redeem'}
                  </Button>
                </form>
                
                <div className="text-center space-y-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAuthMode('login')}
                  >
                    Already have an account? Log in
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setAuthMode('preview')}
                  >
                    Back to options
                  </Button>
                </div>
              </div>
            )}
            
            {error && (
              <Alert variant="destructive" className="mt-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>
        
        {/* Benefits Section */}
        <Card className="bg-white/80 backdrop-blur-sm border-blue-200">
          <CardContent className="p-4">
            <div className="text-center space-y-3">
              <h4 className="font-semibold text-gray-900">Why Vaiu?</h4>
              <div className="grid grid-cols-3 gap-3 text-xs">
                <div className="text-center">
                  <Shield className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                  <p className="font-medium">Secure</p>
                  <p className="text-gray-600">Bank-level security</p>
                </div>
                <div className="text-center">
                  <Smartphone className="w-5 h-5 text-green-600 mx-auto mb-1" />
                  <p className="font-medium">Simple</p>
                  <p className="text-gray-600">Easy to use</p>
                </div>
                <div className="text-center">
                  <Globe className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                  <p className="font-medium">Global</p>
                  <p className="text-gray-600">Works everywhere</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TokenRedemptionLanding; 